import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Sparkles, Bookmark, Share2, Archive as ArchiveIcon, Check } from 'lucide-react';
import { AppShell } from '../components/AppShell';
type FeedItem = {
  id: string;
  type: 'keyword' | 'semantic';
  source: string;
  title: string;
  snippet: string;
  alertOrigin: string;
  time: string;
  unread: boolean;
  dateGroup: string;
};
const initialFeed: FeedItem[] = [{
  id: '1',
  type: 'semantic',
  source: 'ANMAT',
  title: 'Disposición 2341/2026 - Actualización de normativas de trazabilidad',
  snippet: 'Se establecen nuevos requisitos para la trazabilidad de medicamentos oncológicos importados, incluyendo validación en origen...',
  alertOrigin: 'Cambios en trazabilidad',
  time: 'Hace 1 hora',
  unread: true,
  dateGroup: 'Hoy'
}, {
  id: '2',
  type: 'semantic',
  source: 'SENASA',
  title: 'Resolución 876/2026 - Restricciones a principios activos',
  snippet: 'Modificación de la lista de principios activos sujetos a control especial para importación desde países no limítrofes.',
  alertOrigin: 'Importación principios activos',
  time: 'Hace 3 horas',
  unread: true,
  dateGroup: 'Hoy'
}, {
  id: '3',
  type: 'keyword',
  source: 'Boletín Oficial',
  title: 'Boletín Oficial Sección 1 - Pág. 34',
  snippet: '...aprobación del nuevo Vademécum PAMI para el trimestre en curso, con inclusión de nuevas terapias biológicas...',
  alertOrigin: '"Vademécum PAMI"',
  time: 'Hace 5 horas',
  unread: true,
  dateGroup: 'Hoy'
}, {
  id: '4',
  type: 'semantic',
  source: 'ANMAT',
  title: 'Circular 45/2026 - Habilitaciones de depósitos',
  snippet: 'Nuevas directrices para la habilitación de depósitos de medicamentos que requieran cadena de frío estricta.',
  alertOrigin: 'Cambios en trazabilidad',
  time: '14:30',
  unread: true,
  dateGroup: 'Ayer'
}, {
  id: '5',
  type: 'keyword',
  source: 'Min. Salud',
  title: 'Licitación Pública 45/2026',
  snippet: 'Llamado a licitación pública salud para la adquisición de medicamentos antirretrovirales y tratamientos oncológicos...',
  alertOrigin: '"Licitación pública salud"',
  time: '09:15',
  unread: false,
  dateGroup: 'Ayer'
}, {
  id: '6',
  type: 'semantic',
  source: 'INAME',
  title: 'Disposición 2298/2026 - Actualización de farmacopea',
  snippet: 'Actualización de los estándares de calidad requeridos para la importación de principios activos de uso compasivo.',
  alertOrigin: 'Importación principios activos',
  time: '11:00',
  unread: false,
  dateGroup: 'Esta semana'
}, {
  id: '7',
  type: 'keyword',
  source: 'PAMI',
  title: 'Resolución Interna 112/2026',
  snippet: 'Modificación de los topes de cobertura para medicamentos incluidos en el Vademécum PAMI vigentes a partir del mes próximo.',
  alertOrigin: '"Vademécum PAMI"',
  time: 'Lun 14:20',
  unread: false,
  dateGroup: 'Esta semana'
}];
export function InboxPage() {
  const [activeTab, setActiveTab] = useState<'todo' | 'sinLeer' | 'archivado'>('todo');
  const [feed, setFeed] = useState<FeedItem[]>(initialFeed);
  const [savedItems, setSavedItems] = useState<Set<string>>(new Set());
  const unreadCount = feed.filter(i => i.unread).length;
  const handleToggleRead = (id: string) => {
    setFeed(feed.map(item => item.id === id ? {
      ...item,
      unread: !item.unread
    } : item));
  };
  const handleToggleSave = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSavedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const handleArchive = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setFeed(feed.filter(item => item.id !== id));
  };
  const filteredFeed = feed.filter(item => {
    if (activeTab === 'sinLeer') return item.unread;
    if (activeTab === 'archivado') return false; // Mock: no archived items stored
    return true;
  });
  // Group by date
  const groupedFeed = filteredFeed.reduce((acc, item) => {
    if (!acc[item.dateGroup]) acc[item.dateGroup] = [];
    acc[item.dateGroup].push(item);
    return acc;
  }, {} as Record<string, FeedItem[]>);
  return <AppShell data-id="element-734">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-id="element-735">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-736">
          <div data-id="element-737">
            <h1 className="text-2xl text-navy-950 mb-1" data-id="element-738">Bandeja de Entrada</h1>
            <p className="text-sm text-navy-600" data-id="element-739">
              {unreadCount}{' '}
              {unreadCount === 1 ? 'novedad sin leer' : 'novedades sin leer'} en
              sus alertas.
            </p>
          </div>
          <button onClick={() => setFeed(feed.map(i => ({
          ...i,
          unread: false
        })))} className="text-sm font-medium text-brand-500 hover:text-brand-600 transition-colors" data-id="element-740">
            Marcar todo como leído
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-slate-200" data-id="element-741">
          <button onClick={() => setActiveTab('todo')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'todo' ? 'text-brand-600' : 'text-slate-400 hover:text-navy-950'}`} data-id="element-742">
            Todo
            {activeTab === 'todo' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-500 rounded-t-full" data-id="element-743" />}
          </button>
          <button onClick={() => setActiveTab('sinLeer')} className={`pb-3 text-sm font-medium transition-colors relative flex items-center gap-2 ${activeTab === 'sinLeer' ? 'text-brand-600' : 'text-slate-400 hover:text-navy-950'}`} data-id="element-744">
            Sin Leer
            {unreadCount > 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === 'sinLeer' ? 'bg-brand-100 text-brand-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-745">
                {unreadCount}
              </span>}
            {activeTab === 'sinLeer' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-500 rounded-t-full" data-id="element-746" />}
          </button>
          <button onClick={() => setActiveTab('archivado')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'archivado' ? 'text-brand-600' : 'text-slate-400 hover:text-navy-950'}`} data-id="element-747">
            Archivado
            {activeTab === 'archivado' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-500 rounded-t-full" data-id="element-748" />}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-749">
          {Object.keys(groupedFeed).length === 0 ? <div className="p-12 text-center" data-id="element-750">
              <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4" data-id="element-751">
                <Check size={24} className="text-slate-400" data-id="element-752" />
              </div>
              <h3 className="text-base font-medium text-navy-950 mb-1" data-id="element-753">
                Todo al día
              </h3>
              <p className="text-sm text-navy-600" data-id="element-754">
                No hay elementos en esta vista.
              </p>
            </div> : <div className="divide-y divide-slate-100" data-id="element-755">
              {Object.entries(groupedFeed).map(([dateGroup, items]) => <div key={dateGroup} data-id="element-756">
                  <div className="px-5 py-2 bg-slate-50/80 border-b border-slate-100" data-id="element-757">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-758">
                      {dateGroup}
                    </span>
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-759">
                    <AnimatePresence initial={false} data-id="element-760">
                      {items.map(item => <motion.div key={item.id} initial={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0,
                  overflow: 'hidden'
                }} transition={{
                  duration: 0.2
                }} onClick={() => handleToggleRead(item.id)} className={`group relative p-5 flex items-start gap-4 transition-colors cursor-pointer ${item.unread ? 'bg-brand-50/30 hover:bg-brand-50/50' : 'hover:bg-slate-50'}`} data-id="element-761">
                          <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 transition-colors ${item.unread ? 'bg-brand-500' : 'bg-transparent'}`} data-id="element-762" />

                          <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 text-navy-600 shadow-sm" data-id="element-763">
                            {item.type === 'semantic' ? <Sparkles size={16} className="text-brand-500" data-id="element-764" /> : <FileText size={16} data-id="element-765" />}
                          </div>

                          <div className="flex-1 min-w-0" data-id="element-766">
                            <div className="flex items-start justify-between gap-4 mb-1" data-id="element-767">
                              <div className="flex flex-col gap-1.5" data-id="element-768">
                                <div className="flex items-center gap-2" data-id="element-769">
                                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-medium border border-slate-200" data-id="element-770">
                                    {item.source}
                                  </span>
                                  <span className="px-2 py-0.5 rounded-md bg-brand-50 text-brand-600 text-[10px] font-medium border border-brand-100" data-id="element-771">
                                    {item.alertOrigin}
                                  </span>
                                </div>
                                <h3 className={`text-sm truncate pr-8 ${item.unread ? 'font-semibold text-navy-950' : 'font-medium text-navy-800'}`} data-id="element-772">
                                  {item.title}
                                </h3>
                              </div>
                              <span className="text-xs text-slate-400 shrink-0 mt-1" data-id="element-773">
                                {item.time}
                              </span>
                            </div>
                            <p className="text-sm text-navy-600 line-clamp-2 mt-1 pr-24" data-id="element-774">
                              {item.snippet}
                            </p>
                          </div>

                          {/* Quick Actions */}
                          <div className="absolute right-5 bottom-5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 backdrop-blur-sm rounded-lg p-1 shadow-sm border border-slate-200" data-id="element-775">
                            <button onClick={e => handleToggleSave(e, item.id)} className={`p-1.5 rounded-md transition-colors ${savedItems.has(item.id) ? 'text-brand-500 bg-brand-50' : 'text-slate-400 hover:text-navy-950 hover:bg-slate-100'}`} title={savedItems.has(item.id) ? 'Quitar de guardados' : 'Guardar'} data-id="element-776">
                              <Bookmark size={16} className={savedItems.has(item.id) ? 'fill-current' : ''} data-id="element-777" />
                            </button>
                            <button onClick={e => e.stopPropagation()} className="p-1.5 rounded-md text-slate-400 hover:text-navy-950 hover:bg-slate-100 transition-colors" title="Compartir" data-id="element-778">
                              <Share2 size={16} data-id="element-779" />
                            </button>
                            <button onClick={e => handleArchive(e, item.id)} className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Archivar" data-id="element-780">
                              <ArchiveIcon size={16} data-id="element-781" />
                            </button>
                          </div>
                        </motion.div>)}
                    </AnimatePresence>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>
    </AppShell>;
}